// ============================================
// APPLICATION DETAIL PAGE
// ============================================
// TIER-3 UPDATE:
// [ŞİRKET-H1] Sertifika bilgileri eklendi
// [ŞİRKET-H2] Deniz hizmeti kayıtları eklendi
// [ŞİRKET-H3] Referans/Rozet bilgileri eklendi
// [ŞİRKET-H6] İletişim butonu düzenlendi
// ============================================

import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  ArrowLeft, 
  Mail, 
  Phone, 
  MapPin, 
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  MessageSquare,
  Briefcase,
  Award,
  Loader2,
  Anchor,
  Shield,
  Ship,
  Calendar,
  User,
  ExternalLink,
  AlertTriangle,
  BadgeCheck,
  Users,
} from 'lucide-react';
import { PageHeader, StatusBadge, EmptyState } from '@/components';
import { applicationsApi } from '@/services/api';
import { 
  formatDate, 
  formatCurrency, 
  formatRelativeTime,
} from '@/utils';
import { useTranslation } from '@crew-at-sea/i18n';
import { useState } from 'react';

export const Route = createFileRoute('/_authenticated/applications/$id')({
  component: ApplicationDetailPage,
});

// Tab types
type TabType = 'overview' | 'certificates' | 'experience' | 'documents' | 'references';

function ApplicationDetailPage() {
  const { t } = useTranslation(['panel', 'common', 'enums']);
  const { id } = Route.useParams();
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState('');
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  // URL düzeltme: relative path → API URL prefix
  const getDocUrl = (url: string) => {
    if (!url) return '#';
    if (url.startsWith('http')) return url;
    if (url.startsWith('data:')) return url;
    const apiBase = (import.meta.env.VITE_API_URL || '').replace('/api/v1', '');
    return `${apiBase}${url.startsWith('/') ? '' : '/'}${url}`;
  };

  // Fetch application details
  const { data: rawApplication, isLoading, refetch } = useQuery({
    queryKey: ['applications', id],
    queryFn: () => applicationsApi.getById(id),
    enabled: !!id,
  });

  // [ŞİRKET-H1-H3] Fetch detailed seafarer profile
  const { data: rawSeafarerProfile, isLoading: isProfileLoading } = useQuery({
    queryKey: ['applications', id, 'seafarer-profile'],
    queryFn: () => applicationsApi.getSeafarerProfile(id),
    enabled: !!id,
  });

  // Handle backend response wrapper - { success, data: {...} }
  const application = (() => {
    if (!rawApplication) return null;
    const raw = rawApplication as any;
    if (raw.success !== undefined && raw.data) return raw.data;
    return raw;
  })();
  const seafarerProfile = (() => {
    if (!rawSeafarerProfile) return null;
    const raw = rawSeafarerProfile as any;
    if (raw.success !== undefined && raw.data) return raw.data;
    return raw;
  })();

  // Update status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ status, notes }: { status: string; notes?: string }) =>
      applicationsApi.updateStatus(id, { status: status as any, notes }),
    onSuccess: () => {
      refetch();
      queryClient.invalidateQueries({ queryKey: ['applications'] });
    },
  });

  // Update notes mutation
  const updateNotesMutation = useMutation({
    mutationFn: (notes: string) => applicationsApi.updateNotes(id, { notes }),
    onSuccess: () => {
      refetch();
    },
  });

  const handleStatusChange = (status: string) => {
    updateStatusMutation.mutate({ status, notes });
  };

  const handleSaveNotes = () => {
    updateNotesMutation.mutate(notes);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-maritime-600 animate-spin" />
      </div>
    );
  }

  if (!application) {
    return (
      <EmptyState
        icon={<FileText className="w-8 h-8 text-gray-400" />}
        title={t('panel:applications.detail.notFound')}
        description={t('panel:applications.detail.notFoundDesc')}
        action={
          <Link to="/applications" className="btn-primary">
            {t('panel:applications.detail.backToList')}
          </Link>
        }
      />
    );
  }

  const seafarer = seafarerProfile || application.seafarer;
  const job = application.job;

  // Tab data
  const tabs = [
    { id: 'overview' as TabType, label: t('panel:applications.detail.tabs.overview'), icon: User },
    { id: 'certificates' as TabType, label: t('panel:applications.detail.tabs.certificates'), icon: Award, count: seafarerProfile?.certificates?.length },
    { id: 'experience' as TabType, label: t('panel:applications.detail.tabs.experience'), icon: Ship, count: seafarerProfile?.seaServiceRecords?.length },
    { id: 'documents' as TabType, label: t('panel:applications.detail.tabs.documents'), icon: FileText, count: seafarerProfile?.documents?.length },
    { id: 'references' as TabType, label: t('panel:applications.detail.tabs.references'), icon: Users, count: seafarerProfile?.references?.length },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('panel:applications.detail.pageTitle')}
        description={`${seafarer?.firstName} ${seafarer?.lastName} - ${job?.title}`}
        actions={
          <Link to="/applications" className="btn-secondary">
            <ArrowLeft className="w-4 h-4" />
            {t('panel:applications.detail.back')}
          </Link>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Ana İçerik */}
        <div className="lg:col-span-2 space-y-6">
          {/* Aday Başlık Kartı */}
          <div className="bg-white rounded-maritime border border-gray-200 p-6">
            <div className="flex items-start gap-4 mb-6">
              {seafarer?.avatarUrl ? (
                <img 
                  src={seafarer.avatarUrl} 
                  alt={`${seafarer?.firstName} ${seafarer?.lastName}`}
                  className="w-16 h-16 rounded-full object-cover"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-maritime-100 flex items-center justify-center text-xl font-semibold text-maritime-600">
                  {seafarer?.firstName?.[0]}{seafarer?.lastName?.[0]}
                </div>
              )}
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-xl font-semibold text-gray-900">
                    {seafarer?.firstName} {seafarer?.lastName}
                  </h3>
                  {seafarer?.isVerified && (
                    <BadgeCheck className="w-5 h-5 text-blue-500" title={t('panel:applications.detail.verifiedProfile')} />
                  )}
                </div>
                <p className="text-maritime-600">
                  {seafarer?.currentRank?.nameTr || seafarer?.rank?.name || t('panel:applications.detail.noPosition')}
                </p>
                <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-500">
                  {seafarer?.nationality && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {seafarer.nationality}
                    </span>
                  )}
                  {(seafarer?.experienceYears || seafarerProfile?.experienceYears) && (
                    <span className="flex items-center gap-1">
                      <Briefcase className="w-4 h-4" />
                      {t('panel:applications.detail.yearsExperience', { count: seafarerProfile?.experienceYears || seafarer.experienceYears })}
                    </span>
                  )}
                  {seafarerProfile?.totalSeaDays > 0 && (
                    <span className="flex items-center gap-1">
                      <Anchor className="w-4 h-4" />
                      {t('panel:applications.detail.seaDays', { count: seafarerProfile.totalSeaDays })}
                    </span>
                  )}
                </div>
              </div>
              <StatusBadge
                status={application.status}
                label={t(`enums:applicationStatus.${application.status}`)}
              />
            </div>

            {/* [ŞİRKET-H1] Sertifika Özeti */}
            {seafarerProfile?.certificateSummary && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 py-4 border-t border-gray-100">
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">{seafarerProfile.certificateSummary.total}</p>
                  <p className="text-xs text-gray-500">{t('panel:applications.detail.certSummary.total')}</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{seafarerProfile.certificateSummary.verified}</p>
                  <p className="text-xs text-gray-500">{t('panel:applications.detail.certSummary.verified')}</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-yellow-600">{seafarerProfile.certificateSummary.expiringSoon}</p>
                  <p className="text-xs text-gray-500">{t('panel:applications.detail.certSummary.expiringSoon')}</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-600">{seafarerProfile.certificateSummary.expired}</p>
                  <p className="text-xs text-gray-500">{t('panel:applications.detail.certSummary.expired')}</p>
                </div>
              </div>
            )}

            {/* [ŞİRKET-H6] İletişim Bilgileri & Aksiyonlar */}
            {/* [ŞİRKET-R1] Telefon bilgileri sadece onaylanan başvurularda görünür */}
            {(() => {
              const approvedStatuses = ['SHORTLISTED', 'INTERVIEW', 'OFFERED', 'ACCEPTED', 'HIRED'];
              const isApproved = approvedStatuses.includes(application.status);
              return (
                <div className="flex flex-wrap items-center justify-between gap-4 py-4 border-t border-gray-100">
                  <div className="flex flex-wrap gap-4">
                    {(seafarer?.user?.email || seafarer?.email) && (
                      <a 
                        href={`mailto:${seafarer?.user?.email || seafarer?.email}`} 
                        className="flex items-center gap-2 text-sm text-maritime-600 hover:underline"
                      >
                        <Mail className="w-4 h-4" />
                        {seafarer?.user?.email || seafarer?.email}
                      </a>
                    )}
                    {isApproved && seafarer?.phone && (
                      <a 
                        href={`tel:${seafarer.phone}`} 
                        className="flex items-center gap-2 text-sm text-maritime-600 hover:underline"
                      >
                        <Phone className="w-4 h-4" />
                        {seafarer.phone}
                      </a>
                    )}
                    {!isApproved && seafarer?.phone && (
                      <span className="flex items-center gap-2 text-sm text-gray-400">
                        <Phone className="w-4 h-4" />
                        {t('panel:applications.detail.phoneApprovalRequired')}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {isApproved && seafarer?.phone && (
                      <a
                        href={`https://wa.me/${seafarer.phone.replace(/[^0-9]/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-secondary text-sm"
                      >
                        <MessageSquare className="w-4 h-4" />
                        WhatsApp
                      </a>
                    )}
                {/* [ŞİRKET-R5] CV Download Button */}
                {seafarerProfile?.documents?.some((doc: any) => 
                  doc.type === 'CV' || doc.type === 'OTHER' || doc.name?.toLowerCase().includes('cv') || doc.name?.toLowerCase().includes('özgeçmiş')
                ) && (() => {
                  const cvDoc = seafarerProfile.documents.find((doc: any) => 
                    doc.type === 'CV' || doc.type === 'OTHER' || doc.name?.toLowerCase().includes('cv') || doc.name?.toLowerCase().includes('özgeçmiş')
                  );
                  return cvDoc?.fileUrl ? (
                  <a
                    href={getDocUrl(cvDoc.fileUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary text-sm"
                    download
                  >
                    <FileText className="w-4 h-4" />
                    {t('panel:applications.detail.downloadCv')}
                  </a>
                  ) : null;
                })()}
                {/* [ŞİRKET-R5] Profile View Button */}
                {seafarer?.slug && (
                  <a
                    href={`${import.meta.env.VITE_PUBLIC_URL || 'https://crewatsea.com'}/seafarer/${seafarer.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary text-sm"
                  >
                    <User className="w-4 h-4" />
                    {t('panel:applications.detail.viewProfile')}
                  </a>
                )}
                {(seafarer?.user?.email || seafarer?.email) && (
                  <a
                    href={`mailto:${seafarer?.user?.email || seafarer?.email}?subject=${t('panel:applications.detail.emailSubject', { title: job?.title })}`}
                    className="btn-primary text-sm"
                  >
                    <Mail className="w-4 h-4" />
                    {t('panel:applications.detail.sendEmail')}
                  </a>
                )}
              </div>
            </div>
              );
            })()}
          </div>

          {/* Sekmeler */}
          <div className="bg-white rounded-maritime border border-gray-200">
            <div className="border-b border-gray-200">
              <nav className="flex -mb-px overflow-x-auto">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-maritime-600 text-maritime-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                    {tab.count !== undefined && tab.count > 0 && (
                      <span className="ml-1 px-2 py-0.5 text-xs bg-gray-100 rounded-full">
                        {tab.count}
                      </span>
                    )}
                  </button>
                ))}
              </nav>
            </div>

            <div className="p-6">
              {isProfileLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-maritime-600 animate-spin" />
                </div>
              ) : (
                <>
                  {/* Genel Bilgiler Sekmesi */}
                  {activeTab === 'overview' && (
                    <div className="space-y-6">
                      {/* Başvuru Bilgileri */}
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">{t('panel:applications.detail.applicationInfo')}</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <p className="text-sm text-gray-500 mb-1">{t('panel:applications.detail.appliedJob')}</p>
                            <Link 
                              to="/jobs/$slug"
                              params={{ slug: job?.slug }}
                              className="font-medium text-maritime-600 hover:underline"
                            >
                              {job?.title}
                            </Link>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500 mb-1">{t('panel:applications.detail.applicationDate')}</p>
                            <p className="font-medium">{formatDate(application.createdAt)}</p>
                          </div>
                          {application.expectedSalary && (
                            <div>
                              <p className="text-sm text-gray-500 mb-1">{t('panel:applications.detail.expectedSalary')}</p>
                              <p className="font-medium">{formatCurrency(application.expectedSalary)}</p>
                            </div>
                          )}
                          {application.availableFrom && (
                            <div>
                              <p className="text-sm text-gray-500 mb-1">{t('panel:applications.detail.availableFrom')}</p>
                              <p className="font-medium">{formatDate(application.availableFrom)}</p>
                            </div>
                          )}
                          {application.cvUrl && (
                            <div>
                              <p className="text-sm text-gray-500 mb-1">{t('panel:applications.detail.attachedCv', { defaultValue: 'Ekli CV' })}</p>
                              <a 
                                href={application.cvUrl.startsWith('http') ? application.cvUrl : `https://pub-5032e646ffe54d57a0fe939da04324bc.r2.dev/${application.cvUrl}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 text-sm font-medium text-maritime-600 hover:text-maritime-700 bg-maritime-50 px-3 py-1.5 rounded-md"
                              >
                                <FileText className="w-4 h-4" />
                                {t('panel:applications.detail.viewCv', { defaultValue: 'CV Görüntüle' })}
                              </a>
                            </div>
                          )}
                        </div>
                      </div>

                      {application.coverLetter && (
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900 mb-2">{t('panel:applications.detail.coverLetter')}</h4>
                          <p className="text-gray-700 whitespace-pre-line bg-gray-50 p-4 rounded-lg">
                            {application.coverLetter}
                          </p>
                        </div>
                      )}

                      {/* Hakkında */}
                      {seafarerProfile?.bio && (
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900 mb-2">{t('panel:applications.detail.about')}</h4>
                          <p className="text-gray-700 whitespace-pre-line">{seafarerProfile.bio}</p>
                        </div>
                      )}

                      {/* [ŞİRKET-H3] Rozetler */}
                      {Array.isArray(seafarerProfile?.badges) && seafarerProfile.badges.length > 0 && (
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900 mb-3">{t('panel:applications.detail.badges')}</h4>
                          <div className="flex flex-wrap gap-2">
                            {seafarerProfile.badges.map((b: any) => (
                              <div 
                                key={b.badge.id}
                                className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg"
                                style={{ borderLeft: `3px solid ${b.badge.color || '#0ea5e9'}` }}
                              >
                                {b.badge.iconUrl ? (
                                  <img src={b.badge.iconUrl} alt="" className="w-5 h-5" />
                                ) : (
                                  <Award className="w-5 h-5" style={{ color: b.badge.color || '#0ea5e9' }} />
                                )}
                                <span className="text-sm font-medium">{b.badge.nameTr}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* [ŞİRKET-H1] Sertifikalar Sekmesi */}
                  {activeTab === 'certificates' && (
                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-gray-900">{t('panel:applications.detail.certificates.title')}</h4>
                      {Array.isArray(seafarerProfile?.certificates) && seafarerProfile.certificates.length > 0 ? (
                        <div className="space-y-3">
                          {seafarerProfile.certificates.map((cert: any) => {
                            const isExpired = cert.expiryDate && new Date(cert.expiryDate) < new Date();
                            const isExpiringSoon = cert.expiryDate && !isExpired && 
                              Math.ceil((new Date(cert.expiryDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)) <= 90;
                            
                            return (
                              <div 
                                key={cert.id}
                                className={`p-4 rounded-lg border ${
                                  isExpired ? 'border-red-200 bg-red-50' :
                                  isExpiringSoon ? 'border-yellow-200 bg-yellow-50' :
                                  'border-gray-200 bg-gray-50'
                                }`}
                              >
                                <div className="flex items-start justify-between gap-4">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <h5 className="font-medium text-gray-900">
                                        {cert.certificateType?.nameTr || cert.certificateType?.nameEn}
                                      </h5>
                                      {cert.isVerified && (
                                        <Shield className="w-4 h-4 text-green-500" title={t('panel:applications.detail.certificates.verified')} />
                                      )}
                                      {isExpired && (
                                        <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded">
                                          {t('panel:applications.detail.certificates.expired')}
                                        </span>
                                      )}
                                      {isExpiringSoon && (
                                        <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded">
                                          {t('panel:applications.detail.certificates.expiringSoon')}
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-sm text-gray-500 mt-1">
                                      {cert.certificateType?.category}
                                    </p>
                                    <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-600">
                                      {cert.certificateNumber && (
                                        <span>{t('panel:applications.detail.certificates.number', { value: cert.certificateNumber })}</span>
                                      )}
                                      <span>{t('panel:applications.detail.certificates.issuedBy', { value: cert.issuingCountry })}</span>
                                      <span>{t('panel:applications.detail.certificates.issueDate', { value: formatDate(cert.issueDate) })}</span>
                                      {cert.expiryDate && (
                                        <span className={isExpired ? 'text-red-600' : isExpiringSoon ? 'text-yellow-600' : ''}>
                                          {t('panel:applications.detail.certificates.expiryDate', { value: formatDate(cert.expiryDate) })}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  {cert.documentUrl && (
                                    <a
                                      href={getDocUrl(cert.documentUrl)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="btn-secondary text-sm"
                                    >
                                      <ExternalLink className="w-4 h-4" />
                                      {t('panel:applications.detail.certificates.view')}
                                    </a>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-center py-8">{t('panel:applications.detail.certificates.empty')}</p>
                      )}
                    </div>
                  )}

                  {/* [ŞİRKET-H2] Deniz Tecrübesi Sekmesi */}
                  {activeTab === 'experience' && (
                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-gray-900">{t('panel:applications.detail.experience.title')}</h4>
                      {Array.isArray(seafarerProfile?.seaServiceRecords) && seafarerProfile.seaServiceRecords.length > 0 ? (
                        <div className="space-y-4">
                          {seafarerProfile.seaServiceRecords.map((record: any) => (
                            <div 
                              key={record.id}
                              className="p-4 rounded-lg border border-gray-200 bg-gray-50"
                            >
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <Ship className="w-5 h-5 text-maritime-600" />
                                    <h5 className="font-medium text-gray-900">{record.vesselName}</h5>
                                    {record.isVerified && (
                                      <Shield className="w-4 h-4 text-green-500" title={t('panel:applications.detail.certificates.verified')} />
                                    )}
                                  </div>
                                  <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                    <div>
                                      <p className="text-gray-500">{t('panel:applications.detail.experience.position')}</p>
                                      <p className="font-medium">{record.rank?.nameTr || record.rank?.nameEn}</p>
                                    </div>
                                    <div>
                                      <p className="text-gray-500">{t('panel:applications.detail.experience.company')}</p>
                                      <p className="font-medium">{record.companyName}</p>
                                    </div>
                                    <div>
                                      <p className="text-gray-500">{t('panel:applications.detail.experience.vesselType')}</p>
                                      <p className="font-medium">{t(`enums:vesselType.${record.vesselType}`, record.vesselType)}</p>
                                    </div>
                                    <div>
                                      <p className="text-gray-500">{t('panel:applications.detail.experience.flag')}</p>
                                      <p className="font-medium">{record.vesselFlag || '-'}</p>
                                    </div>
                                  </div>
                                  <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-gray-600">
                                    <span className="flex items-center gap-1">
                                      <Calendar className="w-4 h-4" />
                                      {formatDate(record.signOnDate)} - {record.signOffDate ? formatDate(record.signOffDate) : t('panel:applications.detail.experience.ongoing')}
                                    </span>
                                    {record.seaDays && (
                                      <span className="flex items-center gap-1">
                                        <Anchor className="w-4 h-4" />
                                        {t('panel:applications.detail.experience.days', { count: record.seaDays })}
                                      </span>
                                    )}
                                    {record.imoNumber && (
                                      <span>IMO: {record.imoNumber}</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-center py-8">{t('panel:applications.detail.experience.empty')}</p>
                      )}
                    </div>
                  )}

                  {/* Belgeler Sekmesi */}
                  {activeTab === 'documents' && (
                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-gray-900">{t('panel:applications.detail.documents.title')}</h4>
                      {Array.isArray(seafarerProfile?.documents) && seafarerProfile.documents.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {seafarerProfile.documents.map((doc: any) => {
                            return (
                            <a
                              key={doc.id}
                              href={getDocUrl(doc.fileUrl)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors"
                            >
                              <FileText className="w-8 h-8 text-maritime-600" />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-900 truncate">{doc.name}</p>
                                <p className="text-sm text-gray-500">{doc.type}</p>
                              </div>
                              <ExternalLink className="w-4 h-4 text-gray-400" />
                            </a>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-center py-8">{t('panel:applications.detail.documents.empty')}</p>
                      )}
                    </div>
                  )}

                  {/* Referanslar Sekmesi */}
                  {activeTab === 'references' && (
                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-gray-900">{t('panel:applications.detail.references.title')}</h4>
                      {Array.isArray(seafarerProfile?.references) && seafarerProfile.references.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {seafarerProfile.references.map((ref: any) => (
                            <div
                              key={ref.id}
                              className="p-4 rounded-lg border border-gray-200 bg-gray-50"
                            >
                              <div className="flex items-start gap-3">
                                <div className="w-10 h-10 rounded-full bg-maritime-100 flex items-center justify-center flex-shrink-0">
                                  <User className="w-5 h-5 text-maritime-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-gray-900">{ref.fullName}</p>
                                  {ref.position && (
                                    <p className="text-sm text-gray-600">{ref.position}</p>
                                  )}
                                  {ref.company && (
                                    <p className="text-sm text-gray-500">{ref.company}</p>
                                  )}
                                  <div className="mt-2 space-y-1">
                                    {ref.email && (
                                      <a href={`mailto:${ref.email}`} className="flex items-center gap-1.5 text-sm text-maritime-600 hover:text-maritime-700">
                                        <Mail className="w-3.5 h-3.5" />
                                        {ref.email}
                                      </a>
                                    )}
                                    {ref.phone && (
                                      <a href={`tel:${ref.phone}`} className="flex items-center gap-1.5 text-sm text-maritime-600 hover:text-maritime-700">
                                        <Phone className="w-3.5 h-3.5" />
                                        {ref.phone}
                                      </a>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-center py-8">{t('panel:applications.detail.references.empty')}</p>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Dahili Notlar */}
          <div className="bg-white rounded-maritime border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('panel:applications.detail.internalNotes.title')}</h3>
            <textarea
              className="input min-h-[100px]"
              placeholder={t('panel:applications.detail.internalNotes.placeholder')}
              value={notes || application.internalNotes || ''}
              onChange={(e) => setNotes(e.target.value)}
            />
            <div className="flex justify-end mt-3">
              <button
                onClick={handleSaveNotes}
                className="btn-secondary"
                disabled={updateNotesMutation.isPending}
              >
                {updateNotesMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  t('panel:applications.detail.internalNotes.save')
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* İşlemler */}
          <div className="bg-white rounded-maritime border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('panel:applications.detail.sidebar.actions')}</h3>
            <div className="space-y-3">
              {application.status === 'PENDING' && (
                <>
                  <button
                    onClick={() => handleStatusChange('REVIEWED')}
                    className="btn-secondary w-full justify-center"
                    disabled={updateStatusMutation.isPending}
                  >
                    <Clock className="w-4 h-4" />
                    {t('panel:applications.detail.sidebar.markReviewed')}
                  </button>
                  <button
                    onClick={() => handleStatusChange('SHORTLISTED')}
                    className="btn-primary w-full justify-center"
                    disabled={updateStatusMutation.isPending}
                  >
                    <CheckCircle className="w-4 h-4" />
                    {t('panel:applications.detail.sidebar.shortlist')}
                  </button>
                </>
              )}
              
              {application.status === 'REVIEWED' && (
                <button
                  onClick={() => handleStatusChange('SHORTLISTED')}
                  className="btn-primary w-full justify-center"
                  disabled={updateStatusMutation.isPending}
                >
                  <CheckCircle className="w-4 h-4" />
                  {t('panel:applications.detail.sidebar.shortlist')}
                </button>
              )}
              
              {application.status === 'SHORTLISTED' && (
                <button
                  onClick={() => handleStatusChange('INTERVIEW')}
                  className="btn-primary w-full justify-center"
                  disabled={updateStatusMutation.isPending}
                >
                  <MessageSquare className="w-4 h-4" />
                  {t('panel:applications.detail.sidebar.interview')}
                </button>
              )}

              {application.status === 'INTERVIEW' && (
                <button
                  onClick={() => handleStatusChange('OFFERED')}
                  className="btn-primary w-full justify-center"
                  disabled={updateStatusMutation.isPending}
                >
                  <Award className="w-4 h-4" />
                  {t('panel:applications.detail.sidebar.offer')}
                </button>
              )}

              {['PENDING', 'REVIEWED', 'SHORTLISTED', 'INTERVIEW'].includes(application.status) && (
                <button
                  onClick={() => handleStatusChange('REJECTED')}
                  className="btn-danger w-full justify-center"
                  disabled={updateStatusMutation.isPending}
                >
                  <XCircle className="w-4 h-4" />
                  {t('panel:applications.detail.sidebar.reject')}
                </button>
              )}
            </div>
          </div>

          {/* Zaman Çizelgesi */}
          <div className="bg-white rounded-maritime border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('panel:applications.detail.sidebar.timeline')}</h3>
            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                  <FileText className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{t('panel:applications.detail.sidebar.applicationSubmitted')}</p>
                  <p className="text-xs text-gray-500">{formatRelativeTime(application.createdAt)}</p>
                </div>
              </div>
              
              {application.status !== 'PENDING' && (
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                    <Clock className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {t(`enums:applicationStatus.${application.status}`)}
                    </p>
                    <p className="text-xs text-gray-500">{formatRelativeTime(application.updatedAt)}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* İlan Özeti */}
          <div className="bg-white rounded-maritime border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('panel:applications.detail.sidebar.jobSummary')}</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">{t('panel:applications.detail.sidebar.department')}</span>
                <span className="font-medium">{t(`enums:department.${job?.department}`, job?.department)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{t('panel:applications.detail.sidebar.salaryRange')}</span>
                <span className="font-medium">
                  {formatCurrency(job?.salaryMin)} - {formatCurrency(job?.salaryMax)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{t('panel:applications.detail.sidebar.contract')}</span>
                <span className="font-medium">{t('panel:applications.detail.sidebar.months', { count: job?.contractDuration })}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
